import { useEffect, useRef, useState } from 'react';
import { Swords } from 'lucide-react';
import type { SchoolLevel } from '@jeraisonne/db';
import { Button, cn, usePrefersReducedMotion } from '@jeraisonne/ui';
import {
  AXIS_LOCK_PX,
  SWIPE_THRESHOLD_PX,
  slideOffsetPercent,
} from '@/features/decouverte/carousel-nav';
import { DecouverteProofView, PROOF_PANEL } from '@/features/decouverte/decouverte-proofs';
import {
  FUN_SLIDE_MS,
  type FunSlide,
  type FunVisual,
  funSlides,
  funSwipe,
  loopIndex,
} from './fun-carousel';
import { KitDots, KitScreen } from './kit-stage';

/**
 * Le dernier écran de l'histoire de la trousse : ce qui attend l'élève une fois le compte créé, du
 * côté du JEU. Le récap dit ce qu'il emporte (ses Moves), celui-ci pourquoi il aura envie de
 * revenir, et l'écran de fin ce qu'un compte gratuit contient. Aucun prix : on vend l'inscription.
 *
 * Les arguments DÉFILENT tout seuls, chacun avec son visuel (`fun-carousel.ts`). Le défilement
 * s'arrête dès que l'élève prend la main (un glissé, une pastille), au survol souris, onglet caché,
 * et ne démarre jamais en mouvement réduit.
 */

export function FunScreen({
  level,
  onSignup,
  onOtherLevel,
}: {
  level: SchoolLevel | null;
  /** l'inscription, par la présentation de l'app (`/decouverte`) */
  onSignup: () => void;
  /** retour au choix de la classe : on rejoue la démo avec une autre question */
  onOtherLevel: () => void;
}) {
  const [slides] = useState(() => funSlides(level));
  return (
    <KitScreen testId="demo-fun" scroll>
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#f7d068]">
        Ton compte gratuit
      </p>
      <h1 className="font-display mt-2 text-center text-3xl font-bold leading-tight text-white">
        Raisonne <span className="text-[#f7d068]">en t’amusant</span>
      </h1>

      <FunCarousel slides={slides} />

      <div className="mt-4 w-full space-y-2 text-center">
        <Button variant="gold" size="lg" fullWidth onClick={onSignup}>
          OK ! je veux m’inscrire
        </Button>
        <button
          type="button"
          className="min-h-11 w-full text-sm font-medium text-white/60 underline underline-offset-4"
          onClick={onOtherLevel}
        >
          Teste une autre classe
        </button>
      </div>

      <div className="mt-4">
        <KitDots step={4} />
      </div>
    </KitScreen>
  );
}

// ─── Le carrousel ─────────────────────────────────────────────────────────────

function FunCarousel({ slides }: { slides: readonly FunSlide[] }) {
  const count = slides.length;
  const [index, setIndex] = useState(0);
  /** l'élève a pris la main : plus rien ne défile tout seul */
  const [manual, setManual] = useState(false);
  const [hover, setHover] = useState(false);
  const [hidden, setHidden] = useState(false);
  const reduced = usePrefersReducedMotion();
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const axisRef = useRef<'x' | 'y' | null>(null);

  // un onglet caché ne consomme rien (et l'élève ne rate aucun argument)
  useEffect(() => {
    const read = () => setHidden(document.hidden);
    read();
    document.addEventListener('visibilitychange', read);
    return () => document.removeEventListener('visibilitychange', read);
  }, []);

  const running = count > 1 && !manual && !hover && !hidden && !reduced;
  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(() => setIndex((i) => loopIndex(i + 1, count)), FUN_SLIDE_MS);
    return () => window.clearInterval(t);
  }, [running, count]);

  function take(next: number) {
    setManual(true);
    setIndex(next);
  }

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (!event.isPrimary) return;
    startRef.current = { x: event.clientX, y: event.clientY };
    axisRef.current = null;
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const start = startRef.current;
    if (!start || axisRef.current) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    // Verrouille l'axe au premier mouvement franc : un geste vertical fait défiler la page.
    if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
    axisRef.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
  }

  function handlePointerEnd(event: React.PointerEvent<HTMLDivElement>) {
    const start = startRef.current;
    const axis = axisRef.current;
    startRef.current = null;
    axisRef.current = null;
    if (!start || axis !== 'x') return;
    const dx = event.clientX - start.x;
    if (Math.abs(dx) >= SWIPE_THRESHOLD_PX) take(funSwipe(dx, index, count));
  }

  return (
    <div
      className="mt-3 flex w-full flex-1 flex-col justify-center"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div
        role="group"
        aria-roledescription="carrousel"
        aria-label="Ce que tu trouves dans l’app"
        className="w-full touch-pan-y overflow-hidden"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        data-testid="fun-carousel"
      >
        <div
          className="flex transition-transform duration-500 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(${slideOffsetPercent(index)}%)` }}
        >
          {slides.map((slide, i) => (
            <div
              key={slide.id}
              // `overflow-hidden` : l'éventail de SKILLCARDS déborde de sa boîte et empiétait sur l'argument voisin ;
              // les voisins s'estompent et reculent d'un cran : l'œil sait où regarder
              className={cn(
                'w-full shrink-0 overflow-hidden px-1 transition-[opacity,transform] duration-500 ease-out motion-reduce:transition-none',
                i === index ? 'opacity-100' : 'scale-95 opacity-35',
              )}
              aria-hidden={i !== index}
              inert={i !== index || undefined}
              data-testid={`fun-slide-${slide.id}`}
            >
              <div className="flex h-[15.5rem] items-center justify-center">
                <FunVisualView visual={slide.visual} />
              </div>
              <div className="mt-3 h-[6.75rem] text-center">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8ec5ff]">
                  {slide.eyebrow}
                </p>
                <h2 className="font-display mt-1 text-xl font-bold leading-tight text-white">
                  {slide.title} <span className="text-[#f7d068]">{slide.highlight}</span>
                </h2>
                <p className="mt-1 text-sm leading-snug text-white/70">{slide.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-center gap-1.5">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => take(i)}
            aria-label={`${slide.eyebrow} (${i + 1} sur ${count})`}
            aria-current={i === index || undefined}
            className="flex h-7 w-5 items-center justify-center"
          >
            <span
              aria-hidden
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === index ? 'w-4 bg-[#f7d068]' : 'w-1.5 bg-white/25',
              )}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Les deux visuels propres à cet écran ─────────────────────────────────────

function FunVisualView({ visual }: { visual: FunVisual }) {
  if (visual.kind === 'challenge') return <ProofChallenge />;
  if (visual.kind === 'revision') return <ProofRevision />;
  return <DecouverteProofView proof={visual} />;
}

/** Un défi privé en cours : le code à partager, et qui joue. */
function ProofChallenge() {
  const players = [
    { name: 'Toi', score: '4/5', me: true },
    { name: 'Papa', score: '3/5', me: false },
    { name: 'Léa', score: '2/5', me: false },
  ];
  return (
    <div
      aria-hidden
      data-proof="challenge"
      className={cn(PROOF_PANEL, 'pointer-events-none w-[17.5rem] select-none p-3')}
    >
      <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-white">
        <Swords aria-hidden className="h-4 w-4 shrink-0 text-[#ff8b8b]" strokeWidth={2.4} />
        Défi privé · 3 joueurs
      </p>
      <p className="font-display mt-1.5 text-center text-lg font-extrabold tracking-[0.2em] text-[#f7d068]">
        7K2M9Q
      </p>
      <ol className="mt-2 space-y-1">
        {players.map((p) => (
          <li
            key={p.name}
            className={cn(
              'flex items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-sm',
              p.me ? 'bg-[#f7d068] text-[#0a2e57]' : 'bg-white/[0.07] text-white/80',
            )}
          >
            <span className="flex-1 font-semibold">{p.name}</span>
            <span className="font-display font-bold tabular-nums">{p.score}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Une fiche de révision : la règle, quand s'en servir, le piège. */
function ProofRevision() {
  return (
    <div
      aria-hidden
      data-proof="revision"
      className={cn(PROOF_PANEL, 'pointer-events-none w-[17.5rem] select-none p-3')}
    >
      <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-white/70">
        Fiche de révision · Pythagore
      </p>
      <div className="mt-1.5 space-y-1">
        <FicheBlock label="La règle">
          <span className="font-['STIX_Two_Text',serif] text-[15px] text-white">
            BC² = AB² + AC²
          </span>
        </FicheBlock>
        <FicheBlock label="Quand l’utiliser">
          <span className="text-[13px] leading-snug text-white/85">
            Un triangle rectangle, deux longueurs connues.
          </span>
        </FicheBlock>
        <FicheBlock label="Le piège" tone="warn">
          <span className="text-[13px] leading-snug text-white/85">
            L’hypoténuse est toujours le plus grand côté.
          </span>
        </FicheBlock>
      </div>
    </div>
  );
}

function FicheBlock({
  label,
  tone = 'info',
  children,
}: {
  label: string;
  tone?: 'info' | 'warn';
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-white/[0.06] px-2.5 py-1">
      <p
        className={cn(
          'text-[10px] font-extrabold uppercase tracking-[0.12em]',
          tone === 'warn' ? 'text-[#ffb08f]' : 'text-[#8ec5ff]',
        )}
      >
        {label}
      </p>
      {children}
    </div>
  );
}
